/**
 * Opens images in a new window for printing to PDF.
 * Each image is isolated to prevent conflicts and sized to fit one page.
 */
export const generatePDF = async (images) => {
  if (!images || images.length === 0) return null;

  // Build pages array with processed content
  const pages = [];

  for (let i = 0; i < images.length; i++) {
    const { file } = images[i];
    let content = '';

    if (file.type === 'image/svg+xml') {
      // For SVG, read and modify to add unique IDs to prevent conflicts
      let svgText = await file.text();

      // Add unique prefix to all IDs and references to prevent conflicts between SVGs
      const uniqueId = `img${i}_`;
      svgText = svgText.replace(/id="([^"]+)"/g, `id="${uniqueId}$1"`);
      svgText = svgText.replace(/url\(#([^)]+)\)/g, `url(#${uniqueId}$1)`);
      svgText = svgText.replace(/href="#([^"]+)"/g, `href="#${uniqueId}$1"`);

      content = svgText;
    } else {
      // For images, use the blob URL
      content = `<img src="${images[i].preview}" alt="${file.name}" style="display: block; width: 100%; height: auto;" />`;
    }

    pages.push(content);
  }

  // Create HTML content for printing
  const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>PDF Preview - ${images.length} Image(s)</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          html, body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
          }
          
          body {
            background: #f0f0f0;
            font-family: Arial, sans-serif;
          }
          
          .instructions {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background: #4CAF50;
            color: white;
            padding: 15px;
            text-align: center;
            font-size: 18px;
            z-index: 10000;
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          }
          
          .instructions button {
            margin-left: 20px;
            padding: 10px 20px;
            font-size: 16px;
            background: white;
            color: #4CAF50;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-weight: bold;
          }
          
          .page {
            background: white;
            margin: 60px 20px 20px;
            padding: 0;
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
            position: relative;
            min-height: 200px;
          }
          
          .page img,
          .page svg {
            display: block;
            max-width: 100%;
            height: auto;
          }
          
          @media print {
            @page {
              margin: 0;
              size: auto;
            }
            
            html, body {
              background: white;
              margin: 0;
              padding: 0;
            }
            
            .instructions {
              display: none !important;
            }
            
            .page {
              margin: 0 !important;
              padding: 0 !important;
              box-shadow: none !important;
              page-break-after: always !important;
              page-break-inside: avoid !important;
              page-break-before: auto !important;
              break-after: page !important;
              break-inside: avoid !important;
              position: relative;
              width: 100%;
              overflow: visible;
            }
            
            .page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
            
            .page img,
            .page svg {
              display: block !important;
              max-width: 100% !important;
              height: auto !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="instructions">
          <strong>✓ ${images.length} image(s) loaded!</strong> Press <kbd>Ctrl+P</kbd> (or click Print Now) → Choose "Save as PDF"
          <button onclick="window.print()">Print Now</button>
          <button onclick="window.close()">Close</button>
        </div>
        ${pages.map((content, i) => `
          <div class="page" data-page="${i + 1}">
            ${content}
          </div>
        `).join('\n')}
      </body>
    </html>
  `;

  // Open in a new window
  const printWindow = window.open('', '_blank', 'width=1400,height=900');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  } else {
    alert('Please allow popups for this site to use the PDF feature.');
  }

  return true;
};
